<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { computed, ref, watch } from 'vue';
import { stopAllSounds } from '@ilteducation/audio-resources';
import { useLocale } from '@/plugins/translation';
import type { Exercise } from '@ilteducation/exercise-service-types';
import useSnackbar from '@/composables/use-snackbar';
import { ExerciseWithProgress, useExerciseStore } from '@/store/exercises';
import { storeToRefs } from 'pinia';
import QuizContainer from '@/components/quiz/QuizContainer.vue';
import QuizResultsContainer from '@/components/quiz/QuizResultsContainer.vue';
import QuizLoading from '@/components/quiz/QuizLoading.vue';
import QuestionDisplay from '@/components/quiz/QuestionDisplay.vue';
import TheHumanBodyExercise from '@/components/videos/exercises/TheHumanBodyExercise.vue';
import type { Video } from '@ilteducation/videos';
import useQuizStore from '@/store/quiz';
import { useRoute, useRouter } from 'vue-router';
import { useUserContext } from '@ilteducation/ilt-auth';
import QuizterButton from '@/components/quiz/QuizterButton.vue';

type GameType = 'matchThePairs' | 'theHumanBody';
type GameItem = { title: string; description: string; type: GameType };

const skeletonVideos = [
  '1tGpR4OtqV9HBrOc7qEyc', // Skelettet - atlas över benen (grund)
  'x8pW1ktiJ807ziik1X3Qg', // Hur fungerar vårt skelett och våra leder?
  '8SYjj2WFhScgkqx6VLNMM', // Skelettet - atlas över benen (fortsättning)
  'Xv8gGXC5JJItIYLDCRJJQ', // Skelettet - atlas över benen (expert)
  'BESxypK6rOHt1H3qkOAx7', // Skelettet - så funkar det (grund)
  'Nrt09v5BZSpDvytIxNxw3', // Skelettet - så funkar det (fortsättning)
  'yzcccwt4ZDV5Sv2r3x4UU', // Skelettet - så funkar det (expert)
];

const props = defineProps<{
  video: Video;
}>();

defineEmits<{
  (e: 'swap-tab'): void;
}>();

const { t, l } = useLocale();
const { xs, sm, mdAndUp } = useDisplay();
const exerciseStore = useExerciseStore();
const quizStore = useQuizStore();
const { mode, isLoading, isError, currentQuiz } = storeToRefs(quizStore);
const { exercises: storeExercises } = storeToRefs(exerciseStore);
const { snackbarError } = useSnackbar();
const tooltip = ref<Record<string, boolean>>({});
const router = useRouter();
const route = useRoute();
const userContext = useUserContext();

const showQuizter = computed(() => userContext.hasRole('employee'));

const exercises = computed<ExerciseWithProgress[]>(() => storeExercises.value[props.video.resourceId] ?? []);
const quizzes = computed(() => exercises.value.filter((e) => e.type === 'quiz'));
const reflectionTasks = computed(() => exercises.value.filter((e) => e.type === 'reflection'));
const reflectionTasksMerged = computed(() => {
  // Hotfix/failsafe, merge all reflection tasks into one (should always be shown under the same tab)
  const arr = reflectionTasks.value;
  if (arr.length === 0) return [];

  const [first, ...rest] = arr;

  return [
    {
      ...first,
      content: [...(first.content ?? []), ...rest.flatMap((r) => r.content ?? [])],
    },
  ];
});
const findOutMoreTasks = computed(() => exercises.value.filter((e) => e.type === 'find-out-more'));
const findOutMoreTasksMerged = computed(() => {
  // Same merge behavior as reflection tasks — always shown under a single tab
  const arr = findOutMoreTasks.value;
  if (arr.length === 0) return [];

  const [first, ...rest] = arr;

  return [
    {
      ...first,
      content: [...(first.content ?? []), ...rest.flatMap((r) => r.content ?? [])],
    },
  ];
});

const gameItems = computed<GameItem[]>(() => {
  const items: GameItem[] = [];
  if (props.video.keywords.length > 2) {
    items.push({
      title: l.games.match_the_pairs.name,
      description: l.games.match_the_pairs.description,
      type: 'matchThePairs',
    });
  }
  if (skeletonVideos.includes(props.video.id) && mdAndUp.value) {
    items.push({
      title: l.games.the_human_body.name,
      description: l.games.the_human_body.description,
      type: 'theHumanBody',
    });
  }
  return items;
});

const hasSidebar = computed(
  () => reflectionTasksMerged.value.length > 0 || findOutMoreTasksMerged.value.length > 0 || gameItems.value.length > 0,
);

const hasQuizzes = computed(() => quizzes.value.length > 0);

const showQuizNav = computed(
  () =>
    quizzes.value.length > 1 ||
    (quizzes.value.length > 0 &&
      (reflectionTasksMerged.value.length > 0 ||
        findOutMoreTasksMerged.value.length > 0 ||
        gameItems.value.length > 0)),
);

const activeExercise = ref<ExerciseWithProgress>();
const reflectionDialog = ref(false);
const reflectionExercise = ref<ExerciseWithProgress>();
const findOutMoreDialog = ref(false);
const findOutMoreExercise = ref<ExerciseWithProgress>();
const humanBodyOpen = ref(false);

watch(
  currentQuiz,
  () => {
    if (currentQuiz.value) {
      activeExercise.value = currentQuiz.value;
    }
  },
  { immediate: true, deep: true },
);
watch(
  quizzes,
  () => {
    if (quizzes.value.length === 0) {
      return;
    }
    if (mode.value === 'quiz-results') {
      // keep current quiz on results view
      currentQuiz.value = quizzes.value.find((e) => e.id === currentQuiz.value?.id);
      return;
    }
    // next uncompleted quiz
    currentQuiz.value = quizzes.value.filter((q) => !q.progress?.completed)[0] || quizzes.value[0];
    quizStore.reset();
  },
  { immediate: true },
);
const quizSwitchHandler = (item: Exercise) => {
  stopAllSounds();
  currentQuiz.value = item;
  quizStore.reset();
  activeExercise.value = item;
};
const openReflection = (item: ExerciseWithProgress) => {
  stopAllSounds();
  reflectionExercise.value = item;
  reflectionDialog.value = true;
};
const openFindOutMore = (item: ExerciseWithProgress) => {
  stopAllSounds();
  findOutMoreExercise.value = item;
  findOutMoreDialog.value = true;
};
const openGame = (game: GameItem) => {
  stopAllSounds();
  if (game.type === 'matchThePairs') {
    router.push({
      name: 'GamesMatchThePairs',
      params: { videoId: props.video.id },
      query: { subject: route.params.subject },
    });
  } else if (game.type === 'theHumanBody') {
    humanBodyOpen.value = true;
  }
};

const activeQuizIndex = computed(() => quizzes.value.findIndex((q) => q.id === activeExercise.value?.id));

const tabDividerSide = (index: number): '' | 'divider-right' | 'divider-left' => {
  const isUnfocused = (i: number) => {
    const quiz = quizzes.value[i];
    return !!quiz && activeExercise.value?.id !== quiz.id;
  };
  if (!isUnfocused(index)) return '';
  const active = activeQuizIndex.value;
  if (active < index && isUnfocused(index + 1)) return 'divider-right';
  if (active > index && isUnfocused(index - 1)) return 'divider-left';
  return '';
};

const hasContent = computed(() => !!currentQuiz.value && activeExercise.value?.type === 'quiz');

const navigateToQuizter = () => {
  if (currentQuiz.value) {
    router.push({
      name: 'CreateQuizter',
      params: { videoId: currentQuiz.value.resourceId },
      query: { from: router.currentRoute.value.fullPath },
    });
  }
};

watch(
  isError,
  (error) => {
    if (error) {
      snackbarError(t(l.quiz.error_saving_submission));
    }
  },
  { immediate: true },
);
</script>
<template>
  <div
    class="quiz-and-exercises bg-surface d-flex flex-column flex-md-row align-start py-8"
    :class="{ 'has-sidebar': hasSidebar }"
  >
    <div v-if="hasQuizzes" class="quiz-column d-flex flex-column flex-grow-1 ga-10">
      <div
        v-if="showQuizNav || (showQuizter && currentQuiz)"
        class="d-flex align-center flex-wrap flex-sm-nowrap ga-4 ga-sm-10"
      >
        <div
          v-if="showQuizNav"
          class="quiz-tab-group bg-secondary-container d-flex align-center flex-grow-1 rounded-lg"
          :class="{ 'overflow-auto': xs || sm }"
        >
          <div
            v-for="(exercise, contentIndex) in quizzes"
            :key="exercise.id"
            class="quiz-tab-wrapper d-flex align-center h-100"
            :class="{ active: activeExercise?.id === exercise.id }"
          >
            <v-tooltip
              :disabled="!exercise.progress?.locked"
              :location="xs ? 'center' : 'top'"
              :model-value="tooltip[`quiz-${contentIndex}`]"
              attach="main"
              :aria-labelledby="`quiz-${contentIndex}`"
            >
              <template #activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  :id="`quiz-${contentIndex}`"
                  class="quiz-tab d-flex align-center justify-center flex-grow-1 ga-2 py-1 px-4 rounded-lg"
                  :class="[
                    tabDividerSide(contentIndex),
                    { 'bg-primary': activeExercise?.id === exercise.id, locked: exercise.progress?.locked },
                  ]"
                  :disabled="exercise.progress?.locked"
                  @click="quizSwitchHandler(exercise)"
                  :aria-label="
                    exercise.progress?.locked
                      ? 'Quiz ' + (contentIndex + 1) + '. ' + t(l.quiz.locked_quiz_explanation)
                      : 'Quiz ' + (contentIndex + 1)
                  "
                >
                  <v-icon icon="fa:fal fa-comments-question" size="18" />
                  <span class="quiz-tab__label">Quiz {{ contentIndex + 1 }}</span>
                  <v-icon v-if="exercise.progress?.locked" icon="fa:fas fa-lock" size="18" />
                  <v-icon v-if="exercise.progress?.completed" icon="fa:fas fa-circle-check" size="18" />
                </button>
              </template>
              <span>
                {{ t(l.quiz.locked_quiz_explanation) }}
              </span>
            </v-tooltip>
            <v-icon
              v-if="exercise.progress?.locked"
              class="d-md-none ml-2"
              @click="tooltip[`quiz-${contentIndex}`] = !tooltip[`quiz-${contentIndex}`]"
              aria-hidden="true"
              icon="fa:fas fa-info-circle"
              size="20"
            />
          </div>
        </div>
        <div v-if="showQuizter && currentQuiz" class="quizter-slot">
          <QuizterButton @click="navigateToQuizter" />
        </div>
      </div>

      <div
        v-if="hasContent && currentQuiz"
        class="area-content bg-surface-container-lowest align-self-stretch w-100 pa-10 rounded-lg"
      >
        <QuizContainer v-if="mode === 'quiz' && !isLoading" :exercise="currentQuiz"></QuizContainer>
        <QuizResultsContainer
          v-if="mode === 'quiz-results' && !isLoading"
          :exercise="currentQuiz"
          @swap-tab="$emit('swap-tab')"
        >
        </QuizResultsContainer>
        <QuizLoading v-if="isLoading"></QuizLoading>
      </div>
    </div>

    <div
      v-if="hasSidebar"
      class="area-sidebar bg-surface-container-lowest d-flex flex-column align-center ga-5 pt-5 px-6 pb-8 rounded-lg"
    >
      <div v-if="hasQuizzes" class="sidebar-title text-center w-100">{{ t(l.film.tabs.exercises) }}</div>
      <v-btn
        v-for="(exercise, contentIndex) in findOutMoreTasksMerged"
        :key="`find-out-more-${contentIndex}`"
        variant="flat"
        color="secondary-container"
        block
        height="56"
        rounded
        class="text-none sidebar-btn"
        @click="openFindOutMore(exercise)"
      >
        <v-icon class="mr-3" size="14" icon="fa:far fa-magnifying-glass" />
        {{ t(l.find_out_more.button_label) }}
      </v-btn>
      <v-btn
        v-for="(exercise, contentIndex) in reflectionTasksMerged"
        :key="`reflection-${contentIndex}`"
        variant="flat"
        color="secondary-container"
        block
        height="56"
        rounded
        class="text-none sidebar-btn"
        @click="openReflection(exercise)"
      >
        <v-icon class="mr-3" size="14" icon="fa:far fa-cloud" />
        {{ t(l.reflection_questions.button_label) }}
      </v-btn>
      <v-btn
        v-for="game in gameItems"
        :key="game.type"
        variant="flat"
        color="secondary-container"
        block
        height="56"
        rounded
        class="text-none sidebar-btn"
        @click="openGame(game)"
      >
        <v-icon
          class="mr-3"
          size="14"
          :icon="game.type === 'theHumanBody' ? 'fa:far fa-person-limbs-wide' : 'fa:far fa-cards-blank'"
        />
        {{ t(game.title) }}
      </v-btn>
    </div>

    <v-dialog v-model="reflectionDialog" fullscreen aria-labelledby="question_display_title">
      <QuestionDisplay
        v-if="reflectionExercise"
        :exercise="reflectionExercise"
        :title="t(l.reflection_questions.button_label)"
        @close="reflectionDialog = false"
      ></QuestionDisplay>
    </v-dialog>

    <v-dialog v-model="findOutMoreDialog" fullscreen aria-labelledby="question_display_title">
      <QuestionDisplay
        v-if="findOutMoreExercise"
        :exercise="findOutMoreExercise"
        :title="t(l.find_out_more.button_label)"
        @close="findOutMoreDialog = false"
      ></QuestionDisplay>
    </v-dialog>

    <TheHumanBodyExercise v-if="humanBodyOpen" v-model="humanBodyOpen" :video="video" />
  </div>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';

.quiz-and-exercises {
  gap: 88px;

  @media (max-width: 960px) {
    gap: 24px;
  }
}

.quiz-column {
  min-width: 0;
  max-width: 805px;

  @media (max-width: 960px) {
    width: 100%;
    max-width: none;
  }
}

.quiz-tab-group {
  min-width: 0;
  height: 48px;
}

.quiz-tab-wrapper {
  // Inactive tabs hug their content; the active tab fills the remaining space.
  flex: 0 0 auto;
  min-width: 0;

  &.active {
    flex: 1 1 auto;
  }
}

@media (min-width: 960px) {
  .quiz-tab-wrapper:not(.active) {
    flex-shrink: 1;
  }

  .quiz-tab-wrapper.active {
    flex-shrink: 0;
  }
}

.quiz-tab__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quiz-tab {
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  border: none;
  background-color: transparent;
  color: inherit;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  // 2px inside stroke between two adjacent unfocused tabs; the border-radius
  // gives the line its slight curve at the top and bottom.
  &.divider-right {
    border-right: 2px solid rgb(var(--v-theme-outline));
  }

  &.divider-left {
    border-left: 2px solid rgb(var(--v-theme-outline));
  }

  &.locked {
    cursor: not-allowed;
    opacity: 0.85;
  }
}

.quizter-slot {
  flex: 0 0 199px;
  width: 199px;

  @media (max-width: 600px) {
    flex-basis: 100%;
    width: 100%;
  }

  :deep(.quizter-btn) {
    width: 100%;
    min-height: 48px;
    height: 48px;
    border-radius: 16px;
    font-size: 20px;
  }
}

.area-content {
  border: 1px solid rgb(var(--v-theme-outline-variant));
}

.area-sidebar {
  flex: 0 0 311px;
  width: 311px;
  border: 1px solid rgb(var(--v-theme-outline-variant));

  @media (max-width: 960px) {
    flex-basis: auto;
    width: 100%;
  }
}

.sidebar-title {
  font-family: 'Filson-Soft', Helvetica, Arial, sans-serif;
  font-size: 20px;
  line-height: 1;
}

.sidebar-btn {
  font-size: 14px;
  justify-content: flex-start;
}
</style>
